/**
 * <s-product-gallery> — main image stage + scrollable thumbnail strip (templates/product.liquid).
 *
 *   [data-gallery-track]   horizontal snap scroller; one [data-gallery-slide data-media-id] per media
 *   [data-gallery-thumb=i] thumbnail buttons; aria-current marks the visible slide
 *   [data-gallery-prev/next] arrow buttons (desktop)
 *   [data-variant-media]   slides/thumbs that are a variant's own image: hidden until that variant is picked,
 *                           then shown first (shared photos always show). Products without shared photos keep every image.
 *   [data-variant-media-id] rendered inside the 'product-form' partial; when the variant changes
 *                           and it points at another image, the stage jumps to that image.
 */
class SProductGallery extends HTMLElement {
  #track = null;
  #thumbStrip = null;
  #index = 0;
  #frame = 0;
  #variantObserver = null;
  #lastVariantMedia = '';
  #filtered = false;
  #shown = false;
  #onPickerPointer = null;
  #variantRoot = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    this.#thumbStrip = this.querySelector('[data-gallery-thumbs]');
    if (!this.#track) return;

    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('click', this.#onClick);
    this.querySelectorAll('[data-inline-zoom]').forEach((surface) => {
      const image = surface.querySelector('img');
      if (!image) return;
      surface.onpointermove = (event) => {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        const rect = surface.getBoundingClientRect();
        image.style.transformOrigin = `${(event.clientX - rect.left) / rect.width * 100}% ${(event.clientY - rect.top) / rect.height * 100}%`;
        image.style.transform = 'scale(2)';
      };
      surface.onpointerleave = () => { image.style.transform = ''; image.style.transformOrigin = ''; };
    });
    this.#thumbStrip?.addEventListener('scroll', this.#updateThumbArrows, { passive: true });
    requestAnimationFrame(this.#updateThumbArrows);
    window.addEventListener('resize', this.#onResize);

    const variantRoot = this.closest('.product-grid') || document;
    const currentVariantMedia = () => variantRoot.querySelector('[data-variant-media-id]')?.dataset.variantMediaId || '';
    // Variant-aware gallery: slides marked [data-variant-media] stay hidden until the shopper picks that variant.
    this.#filtered = Boolean(this.querySelector('[data-variant-media]'));
    this.#lastVariantMedia = currentVariantMedia();
    this.#variantObserver = new MutationObserver(() => {
      const id = currentVariantMedia();
      if (id === this.#lastVariantMedia) return;
      this.#lastVariantMedia = id;
      if (this.#filtered) this.#showVariant(id);
      else if (id) this.goToMedia(id);
    });
    this.#variantObserver.observe(variantRoot, { childList: true, subtree: true });

    // Tapping the tile that is already selected (first visit, no ?variant=) still reveals its image.
    this.#onPickerPointer = (event) => {
      if (!this.#filtered || this.#shown || !event.target.closest('s-variant-picker')) return;
      const input = event.target.closest('label')?.control || event.target.closest('input[type="radio"]');
      if (input?.checked) this.#showVariant(currentVariantMedia());
    };
    this.#variantRoot = variantRoot;
    variantRoot.addEventListener('pointerdown', this.#onPickerPointer);

    // Deep link with ?variant=: start on that variant's image.
    const deepLinked = new URL(window.location.href).searchParams.has('variant');
    if (this.#filtered) {
      if (deepLinked) this.#showVariant(this.#lastVariantMedia);
    } else if (this.#lastVariantMedia) {
      this.goToMedia(this.#lastVariantMedia, false);
    }
  }

  /** Show only the given variant's image (first in the stage and strip) next to the shared photos. */
  #showVariant(mediaId) {
    const id = String(mediaId || '');
    this.querySelectorAll('[data-gallery-slide][data-variant-media], [data-gallery-thumb][data-variant-media]').forEach((node) => {
      node.hidden = node.dataset.mediaId !== id;
    });
    if (id) {
      const slide = this.#track.querySelector(`:scope > [data-gallery-slide][data-media-id="${id}"]`);
      if (slide) this.#track.prepend(slide);
      const thumb = this.#thumbStrip?.querySelector(`[data-gallery-thumb][data-media-id="${id}"]`);
      if (thumb) this.#thumbStrip.prepend(thumb);
    }
    this.#shown = true;
    this.#track.scrollTo({ left: 0, behavior: 'instant' });
    this.#thumbStrip?.scrollTo({ left: 0, behavior: 'instant' });
    this.#setActive(0);
    this.#updateThumbArrows();
    this.dispatchEvent(new CustomEvent('gallery:change'));
  }

  disconnectedCallback() {
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#thumbStrip?.removeEventListener('scroll', this.#updateThumbArrows);
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener('resize', this.#onResize);
    this.#variantObserver?.disconnect();
    if (this.#onPickerPointer) this.#variantRoot?.removeEventListener('pointerdown', this.#onPickerPointer);
    cancelAnimationFrame(this.#frame);
  }

  get #slides() {
    return [...this.#track.querySelectorAll(':scope > [data-gallery-slide]:not([hidden])')];
  }

  get #thumbs() {
    return [...this.querySelectorAll('[data-gallery-thumb]:not([hidden])')];
  }

  goTo(index, smooth = true) {
    const slides = this.#slides;
    if (!slides.length) return;
    const count = slides.length;
    const target = ((index % count) + count) % count;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const direction = getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1;
    this.#track.scrollTo({ left: direction * target * this.#track.clientWidth, behavior: smooth && !reduce ? 'smooth' : 'instant' });
    this.#setActive(target);
  }

  goToMedia(mediaId, smooth = true) {
    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.goTo(index, smooth);
  }

  #onClick = (event) => {
    const panel = this.querySelector('[data-stage-video]');
    const video = panel?.querySelector('video');
    if (event.target.closest('[data-stage-play]')) {
      panel.hidden = false;
      if (video) { video.setAttribute('playsinline', ''); video.play().catch(() => {}); }
      panel.querySelector('[data-stage-close]')?.focus({ preventScroll: true });
      return;
    }
    if (event.target.closest('[data-stage-close], [data-gallery-thumb], [data-gallery-prev], [data-gallery-next]')) {
      video?.pause();
      if (panel) panel.hidden = true;
      if (event.target.closest('[data-stage-close]')) {
        this.querySelector('[data-stage-play]')?.focus({ preventScroll: true });
        return;
      }
    }
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) return this.goTo(this.#thumbs.indexOf(thumb));
    if (event.target.closest('[data-gallery-prev]')) return this.goTo(this.#index - 1);
    if (event.target.closest('[data-gallery-next]')) return this.goTo(this.#index + 1);
    if (event.target.closest('[data-thumbs-prev]')) return this.#scrollThumbs(-1);
    if (event.target.closest('[data-thumbs-next]')) return this.#scrollThumbs(1);
  };

  /** Scroll the thumbnail strip by one thumbnail (plus gap) in the given direction. */
  #scrollThumbs(step) {
    const strip = this.#thumbStrip;
    const thumb = strip?.querySelector('[data-gallery-thumb]');
    if (!strip || !thumb) return;
    const gap = parseFloat(getComputedStyle(strip).columnGap) || 0;
    const rtl = getComputedStyle(strip).direction === 'rtl' ? -1 : 1;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    strip.scrollBy({ left: step * rtl * (thumb.offsetWidth + gap), behavior: reduce ? 'instant' : 'smooth' });
  }

  #updateThumbArrows = () => {
    const strip = this.#thumbStrip;
    if (!strip) return;
    const max = strip.scrollWidth - strip.clientWidth;
    const position = Math.abs(strip.scrollLeft);
    const prev = this.querySelector('[data-thumbs-prev]');
    const next = this.querySelector('[data-thumbs-next]');
    if (prev) prev.disabled = position <= 1;
    if (next) next.disabled = position >= max - 1;
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.#track.clientWidth || 1;
      const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
      if (index !== this.#index) this.#setActive(index);
    });
  };

  #onResize = () => {
    this.#updateThumbArrows();
    // Keep the current slide aligned after the stage changes width.
    this.#track.scrollTo({ left: this.#index * this.#track.clientWidth * (getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1), behavior: 'instant' });
  };

  #setActive(index) {
    this.#index = index;
    const thumbs = this.#thumbs;
    thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    // Keep the active thumbnail visible inside the strip without scrolling the page.
    const active = thumbs[index];
    if (active && this.#thumbStrip) {
      const strip = this.#thumbStrip;
      const left = active.offsetLeft - strip.offsetLeft;
      const right = left + active.offsetWidth;
      if (left < strip.scrollLeft || right > strip.scrollLeft + strip.clientWidth) {
        strip.scrollTo({ left: left - (strip.clientWidth - active.offsetWidth) / 2, behavior: 'smooth' });
      }
    }

    // Pause videos that scrolled out of view.
    this.#slides.forEach((slide, i) => {
      if (i === index) return;
      slide.querySelectorAll('video').forEach((video) => video.pause());
    });
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
